import React, { useEffect, useState } from 'react'
import { PoweroffOutlined } from '@ant-design/icons'
import type { ItemType } from 'antd/es/menu/interface'
import { useCompany } from '../contexts/CompanyContext'
import { useUser } from '../contexts/UserContext'
import { useConfig } from '../contexts/ConfigContext'
import { useSecureRequest } from '../hooks/useSecureRequest'
import LogoutModal from './LogoutModal'
import { TopBar as SharedTopBar } from '@ryo/ui'

function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>
  return function (...args: Parameters<T>) {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

const userMenuItems: ItemType[] = [
  {
    key: 'my_user',
    label: 'Mi usuario',
    disabled: true,
  },
  {
    type: 'divider',
  },
  {
    key: 'exit_option',
    label: 'Salir',
    icon: <PoweroffOutlined />,
  },
]

function TopBar() {
  const { companies, updateCompanies } = useCompany()
  const { config, updateConfig } = useConfig()
  const { user } = useUser()
  const { securePost } = useSecureRequest()
  const [fetchedCompanyId, setFetchedCompanyId] = useState<string | number | null>(null)

  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768
      updateConfig({ sideVisible: !isMobile, isMobile, sideCollapsed: isMobile })
    }
    const debouncedResize = debounce(handleResize, 200)
    debouncedResize()
    window.addEventListener('resize', debouncedResize)
    return () => window.removeEventListener('resize', debouncedResize)
  }, [updateConfig])

  // Cargar módulos configurados del usuario para la empresa activa (fallback si no vienen pre-cargados)
  useEffect(() => {
    const fetchUserCompanyModules = async () => {
      const currentCompany = companies.current
      const currentCompanyId = currentCompany?.id || currentCompany?.uid
      const userId = user.id || user.uid

      const hasModules = Array.isArray(currentCompany?.modules) && currentCompany.modules.length > 0

      if (currentCompanyId && userId && config.key && !hasModules && fetchedCompanyId !== currentCompanyId) {
        try {
          const res = await securePost('/user-management/get-user-companies-with-modules', {
            session: config.session,
            user_id: userId
          })
          if (res && Array.isArray(res.companies)) {
            const matchedCompany = res.companies.find((c: any) => 
              String(c.id) === String(currentCompanyId) || String(c.uid) === String(currentCompanyId)
            ) || res.companies[0]

            const userModules = (matchedCompany?.assignedModules && matchedCompany.assignedModules.length > 0)
              ? matchedCompany.assignedModules
              : (matchedCompany?.availableModules || [])

            setFetchedCompanyId(currentCompanyId)
            updateCompanies({
              current: {
                ...currentCompany,
                modules: userModules
              }
            })
          } else {
            setFetchedCompanyId(currentCompanyId)
          }
        } catch (err) {
          console.error('Error al cargar módulos del usuario para la empresa:', err)
          setFetchedCompanyId(currentCompanyId)
        }
      }
    }
    fetchUserCompanyModules()
  }, [companies.current?.id, companies.current?.uid, companies.current?.modules, fetchedCompanyId, user.id, user.uid, config.key, config.session, securePost, updateCompanies])

  const toggleMobileMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!config.sideVisible && config.isMobile) {
      updateConfig({ sideCollapsed: true })
    }
    updateConfig({ sideVisible: !config.sideVisible })
  }

  const onUserItemClick = (key: string) => {
    if (key === 'exit_option') {
      updateConfig({ showLogout: true })
    }
  }

  const onCompanyChange = async (key: string) => {
    const selected = companies.list.find((c: any) => String(c.uid || c.id) === key)
    if (selected) {
      const selectedId = selected.id || selected.uid
      const userId = user.id || user.uid
      try {
        const res = await securePost('/user-management/get-user-companies-with-modules', {
          session: config.session,
          user_id: userId
        })
        if (res && Array.isArray(res.companies)) {
          const matchedCompany = res.companies.find((c: any) => 
            String(c.id) === String(selectedId) || String(c.uid) === String(selectedId)
          ) || res.companies[0]

          const userModules = (matchedCompany?.assignedModules && matchedCompany.assignedModules.length > 0)
            ? matchedCompany.assignedModules
            : (matchedCompany?.availableModules || [])

          setFetchedCompanyId(selectedId)
          updateCompanies({ current: { ...selected, modules: userModules } })
        } else {
          updateCompanies({ current: selected })
        }
      } catch (err) {
        console.error('Error al cambiar de empresa:', err)
        updateCompanies({ current: selected })
      }
    }
  }

  const onModuleClick = (key: string) => {
    const selectedModule = companies.current?.modules?.find((_: any, index: number) => index.toString() === key)
    if (selectedModule?.path) {
      const companyUid = companies.current?.uid || companies.current?.id || ''
      const separator = selectedModule.path.includes('?') ? '&' : '?'
      window.location.href = `${selectedModule.path}${separator}session=${config.session}&company=${companyUid}`
    }
  }

  const formattedCompany = {
    current: {
      uid: String(companies.current?.uid || companies.current?.id || ''),
      name: companies.current?.name || 'Administración',
      icon: companies.current?.icon,
      modules: companies.current?.modules || [],
    },
    list: companies.list.map((c: any) => ({
      uid: String(c.uid || c.id),
      name: c.name,
      icon: c.icon,
      image: c.image,
    })),
  }

  return (
    <>
      <SharedTopBar
        user={{
          name: user.name || 'Administrador',
          permissions: user.permissions || { views: [] },
        }}
        company={formattedCompany as any}
        config={config}
        userMenuItems={userMenuItems}
        onToggleMobileMenu={toggleMobileMenu}
        onCompanyChange={onCompanyChange}
        onModuleClick={onModuleClick}
        onUserItemClick={onUserItemClick}
      />
      <LogoutModal />
    </>
  )
}

export default TopBar
